import { forwardRef } from 'react';
import Icona from '../../../components/3catinfo/ui/Icona/Icona';
import Enllac from '../../../components/3catinfo/Enllac';
import clsx from 'clsx';
import __isEmpty from 'lodash/isEmpty';
import styles from './boto.module.scss';

const Boto = forwardRef((props, ref) => {
    const {
        tag,
        text,
        ariaLabel,
        tema,
        fons,
        estil,
        novaFinestra,
        rel,
        mida,
        link,
        linkTipus,
        linkId,
        linkSlug,
        slug,
        slug2,
        slugPrograma,
        tipologia,
        tipologiaRelacionada,
        canals,
        type,
        format,
        icona,
        altIcona,
        iconaInline,
        isIconaDreta,
        isIconaBlanc,
        id,
        onClick,
        onMouseEnter,
        onMouseLeave,
        onKeyDown,
        onFocus,
        onBlur,
        name,
        className,
        dataTestId,
        data_testid,
        cleanUrlParams,
        tagLabel,
        ariaPressed,
        ariaHiddenBtn,
        ariaLabelBtn
    } = props;

    const dataTestAttribute = dataTestId || data_testid;
    let { route } = props;
    const TagLabel = tagLabel || 'span';
    const estilArray = estil ? estil.split(' ') : [];

    const stylesBoto = clsx(
        estil !== 'noEstil' && styles.boto,
        estilArray.map((current) => styles[current]).filter(Boolean),
        tema && styles[tema],
        mida && styles[mida],
        fons && styles[fons],
        icona && styles[icona],
        !__isEmpty(className) && className
    );

    const defaultRoute = {
        href: link,
        slug: linkSlug || slug,
        slugPrograma: slugPrograma,
        slugRelacio: slug2,
        id: linkId,
        tipologia: tipologia,
        tipologiaRelacionada: tipologiaRelacionada,
        canals: canals,
        type: type || linkTipus,
        format: format
    };

    // Funció reutilitzable per crear la icona
    const createIcon = (isIconaDreta) => {
        if (!icona) return null;

        const iconPositionClass = isIconaDreta ? styles.dreta : '';
        const icon = (
            <Icona
                className={`${styles.icona} ${iconPositionClass}`}
                icona={icona}
                inline={iconaInline}
                alt={altIcona}
                mida={mida}
                ariaHidden={__isEmpty(altIcona)}
                isBlanc={isIconaBlanc}
            />
        );

        return (
            <span className={`${styles.iconWrapper}`}>
                <span className={`${styles.iconaPrincipal}`}>{icon}</span>
            </span>
        );
    };

    // Funció reutilitzable per crear la icona amb funcionalitat de click
    const createClickableIcon = (isIconaDreta) => {
        if (!icona) return null;

        const iconPositionClass = isIconaDreta ? styles.dreta : '';
        const icon = (
            <Icona
                className={`${styles.icona} ${iconPositionClass}`}
                icona={icona}
                inline={iconaInline}
                alt={altIcona}
                mida={mida}
                ariaHidden={__isEmpty(altIcona)}
                isBlanc={isIconaBlanc}
            />
        );

        const handleKeyDown = (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && typeof onClick === 'function') {
                onClick(e);
            }
        };

        return (
            <div
                className={`${styles.iconWrapper}`}
                onClick={onClick}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role='button'
                aria-label='Icon'
            >
                <div className={`${styles.iconaPrincipal}`}>{icon}</div>
            </div>
        );
    };

    const _buttonContent = (text, icona, isIconaDreta) => {
        if (icona) {
            const leftIcon = !isIconaDreta ? createIcon(false) : null;
            const rightIcon = isIconaDreta ? createIcon(true) : null;

            return (
                <>
                    {leftIcon}
                    {text && (
                        <TagLabel data-testid={dataTestAttribute} className={styles.label}>
                            {text}
                        </TagLabel>
                    )}
                    {rightIcon}
                </>
            );
        } else {
            return <TagLabel className={styles.label}>{text}</TagLabel>;
        }
    };

    const _buttonContentRouted = (text, icona, isIconaDreta) => {
        // Assegurar que route es defineix correctament
        const currentRoute = route || defaultRoute;

        if (icona) {
            const leftIcon = !isIconaDreta ? createClickableIcon(false) : null;
            const rightIcon = isIconaDreta ? createClickableIcon(true) : null;

            return (
                <>
                    {leftIcon}
                    {text && (
                        <Enllac
                            route={currentRoute}
                            className={stylesBoto}
                            dataTestId={dataTestAttribute}
                            novaFinestra={novaFinestra}
                            rel={rel}
                            cleanUrlParams={cleanUrlParams}
                        >
                            <TagLabel data-testid={dataTestAttribute} className={styles.label}>
                                {text}
                            </TagLabel>
                        </Enllac>
                    )}
                    {rightIcon}
                </>
            );
        } else {
            return (
                <Enllac
                    route={currentRoute}
                    className={stylesBoto}
                    dataTestId={dataTestAttribute}
                    novaFinestra={novaFinestra}
                    rel={rel}
                    cleanUrlParams={cleanUrlParams}
                >
                    <TagLabel data-testid={dataTestAttribute} className={styles.label}>
                        {text}
                    </TagLabel>
                </Enllac>
            );
        }
    };

    const _buttonLink = () => {
        const currentRoute = route || defaultRoute;

        return (
            <Enllac
                route={currentRoute}
                className={stylesBoto}
                dataTestId={dataTestAttribute}
                novaFinestra={novaFinestra}
                rel={rel}
                cleanUrlParams={cleanUrlParams}
                ariaLabel={ariaLabel}
            >
                {_buttonContent(text, icona, isIconaDreta)}
            </Enllac>
        );
    };

    const _button = () => {
        return (
            <button
                ref={ref}
                className={stylesBoto}
                type={type}
                id={id}
                name={name}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                data-testid={dataTestAttribute}
                aria-pressed={ariaPressed}
                aria-hidden={ariaHiddenBtn}
                aria-label={ariaLabelBtn}
            >
                {_buttonContent(text, icona, isIconaDreta)}
            </button>
        );
    };

    const _buttonRouted = () => {
        return (
            <button ref={ref} className={stylesBoto} type={type} id={id} name={name} data-testid={dataTestAttribute}>
                {_buttonContentRouted(text, icona, isIconaDreta)}
            </button>
        );
    };

    let content;

    if (tag === 'button') {
        content = _button();
    } else if (tag === 'divContentRouted') {
        content = _buttonRouted();
    } else {
        content = _buttonLink();
    }

    return content;
});

Boto.defaultProps = {
    tag: 'a',
    estil: 'ple',
    altIcona: '',
    mida: 16,
    isIconaDreta: true,
    onClick: undefined,
    iconaInline: false,
    isIconaBlanc: false,
    tagLabel: 'span'
};

Boto.displayName = 'Boto';

export default Boto;
